<template>
    <div class="schema-form">
        <template v-for="field in visibleFields" :key="field.key">
            <fieldset v-if="field.kind === 'object'" class="schema-form-group">
                <legend>{{ field.label }}</legend>
                <small v-if="field.description" class="schema-form-help">{{ field.description }}</small>
                <SchemaForm :schema="field.schema" :modelValue="values[field.key] || {}"
                            :context="rootContext" :loadOptions="loadOptions" :disabled="disabled"
                            :showErrors="showErrors" :idPrefix="`${idPrefix}${field.key}-`"
                            @update:modelValue="value => update(field.key, value)"/>
            </fieldset>

            <div v-else class="schema-form-field" :data-cy="`schema-field-${field.key}`">
                <label :for="inputId(field)" :class="{'p-error': isInvalid(field)}">
                    {{ field.label }}<span v-if="field.required"> *</span>
                </label>

                <Select v-if="field.kind === 'options'" :inputId="inputId(field)" :modelValue="values[field.key]"
                        :options="optionItems(field)" optionLabel="label" optionValue="value"
                        editable filter showClear :disabled="disabled" :loading="optionState[field.key]?.loading"
                        :placeholder="optionPlaceholder(field)" :invalid="isInvalid(field)"
                        @update:modelValue="value => update(field.key, value ?? '')"/>

                <Select v-else-if="field.kind === 'select'" :inputId="inputId(field)" :modelValue="values[field.key]"
                        :options="field.schema.enum" :disabled="disabled" :invalid="isInvalid(field)"
                        @update:modelValue="value => update(field.key, value)"/>

                <ToggleSwitch v-else-if="field.kind === 'boolean'" :inputId="inputId(field)"
                              :modelValue="Boolean(values[field.key])" :disabled="disabled"
                              @update:modelValue="value => update(field.key, value)"/>

                <InputNumber v-else-if="field.kind === 'number'" :inputId="inputId(field)"
                             :modelValue="values[field.key] ?? null" :useGrouping="false"
                             :min="field.schema.minimum" :disabled="disabled" :invalid="isInvalid(field)"
                             @update:modelValue="value => update(field.key, value)"/>

                <InputText v-else-if="field.kind === 'secret'" :id="inputId(field)" type="password"
                           autocomplete="new-password" :modelValue="secretInput(field)" :disabled="disabled"
                           :placeholder="isStoredSecret(values[field.key]) ? 'Stored - type to replace' : ''"
                           :invalid="isInvalid(field)"
                           @update:modelValue="value => updateSecret(field.key, value)"/>

                <Textarea v-else-if="field.kind === 'textarea'" :id="inputId(field)" :modelValue="values[field.key] || ''"
                          rows="4" autoResize :disabled="disabled" :invalid="isInvalid(field)"
                          @update:modelValue="value => update(field.key, value)"/>

                <InputText v-else-if="field.kind === 'list'" :id="inputId(field)"
                           :modelValue="(values[field.key] || []).join(', ')" :disabled="disabled"
                           placeholder="Comma separated"
                           @update:modelValue="value => update(field.key, splitList(value))"/>

                <InputText v-else :id="inputId(field)" :modelValue="values[field.key] ?? ''" :disabled="disabled"
                           :invalid="isInvalid(field)" @update:modelValue="value => update(field.key, value)"/>

                <small v-if="optionState[field.key]?.error" class="p-error">{{ optionState[field.key].error }}</small>
                <small v-else-if="field.description" class="schema-form-help">{{ field.description }}</small>
            </div>
        </template>

        <Button v-if="hasAdvanced" text size="small" class="schema-form-advanced"
                :label="showAdvanced ? 'Hide advanced settings' : 'Show advanced settings'"
                :icon="showAdvanced ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                @click="showAdvanced = !showAdvanced"/>
    </div>
</template>

<script setup>
// A form for a pac-eventio JSON Schema (connector connection, input options or output options).
// Dropdowns marked x-options are filled through `loadOptions(provider, params)` once the fields
// they depend on have values; see src/utils/connectorSchema.js for the rules.
import {computed, reactive, ref, watch} from 'vue';
import Button from 'primevue/button';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Textarea from 'primevue/textarea';
import ToggleSwitch from 'primevue/toggleswitch';
import {fieldsOf, isStoredSecret, optionRequest} from '@/src/utils/connectorSchema';
import {isApiError} from '@/src/api/http';

const props = defineProps({
    schema: {type: Object, required: true},
    modelValue: {type: Object, default: () => ({})},
    // values of the outermost form: nested objects (e.g. the column mapping) depend on its fields
    context: {type: Object, default: null},
    loadOptions: {type: Function, default: null},
    exclude: {type: Array, default: () => []},
    disabled: {type: Boolean, default: false},
    showErrors: {type: Boolean, default: false},
    idPrefix: {type: String, default: 'schema-'},
});

const emit = defineEmits(['update:modelValue']);

const values = computed(() => props.modelValue || {});
const rootContext = computed(() => props.context || values.value);
const showAdvanced = ref(false);
const fields = computed(() => fieldsOf(props.schema, values.value, {exclude: props.exclude}));
const hasAdvanced = computed(() => fields.value.some(field => field.advanced));
const visibleFields = computed(() => fields.value.filter(field => showAdvanced.value || !field.advanced));

const inputId = (field) => `${props.idPrefix}${field.key}`;

const isEmptyValue = (value) => value === undefined || value === null || value === '' ||
    (Array.isArray(value) && value.length === 0);
const isInvalid = (field) => props.showErrors && field.required && isEmptyValue(values.value[field.key]);

const update = (key, value) => emit('update:modelValue', {...values.value, [key]: value});

const splitList = (text) => text.split(',').map(item => item.trim()).filter(Boolean);

// A stored secret is shown as an empty password field; clearing what was typed restores it.
const storedSecrets = {};
const secretInput = (field) => (isStoredSecret(values.value[field.key]) ? '' : values.value[field.key] || '');
const updateSecret = (key, typed) => {
    if (isStoredSecret(values.value[key])) storedSecrets[key] = values.value[key];
    update(key, typed === '' && storedSecrets[key] !== undefined ? storedSecrets[key] : typed);
};

// options per field: {items, loading, error, loadedFor}
const optionState = reactive({});

const optionItems = (field) => {
    const items = optionState[field.key]?.items || [];
    const current = values.value[field.key];
    if (current && !items.some(item => item.value === current)) {
        return [{label: String(current), value: current}, ...items];
    }
    return items;
};

const optionPlaceholder = (field) => {
    const request = optionRequest(field.options, rootContext.value);
    if (!props.loadOptions) return '';
    if (!request.ready) {
        const labels = (field.options.depends_on || []).map(key => props.schema.properties?.[key]?.title || key);
        return labels.length ? `Choose ${labels.join(', ')} first` : '';
    }
    return 'Select or type a value';
};

const loadFieldOptions = async (field) => {
    const request = optionRequest(field.options, rootContext.value);
    if (!optionState[field.key]) optionState[field.key] = {items: [], loading: false, error: ''};
    // read back through the reactive object: the plain object assigned above is not tracked
    const state = optionState[field.key];
    if (!props.loadOptions || !request.ready || state.loadedFor === request.dependencyKey) return;
    state.loadedFor = request.dependencyKey;
    state.loading = true;
    state.error = '';
    try {
        const items = await props.loadOptions(request.provider, request.params);
        if (state.loadedFor === request.dependencyKey) state.items = items || [];
    } catch (error) {
        state.items = [];
        state.error = isApiError(error) ? error.message : 'Could not load the values.';
    } finally {
        state.loading = false;
    }
};

watch(
    () => visibleFields.value
        .filter(field => field.kind === 'options')
        .map(field => [field.key, optionRequest(field.options, rootContext.value).dependencyKey]),
    () => visibleFields.value.filter(field => field.kind === 'options').forEach(loadFieldOptions),
    {immediate: true, deep: true}
);
</script>

<style scoped>
.schema-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.schema-form-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.schema-form-field label {
    font-weight: 700;
}

.schema-form-group {
    border: 1px solid var(--p-content-border-color);
    border-radius: 6px;
    padding: 0.5rem 0.75rem 0.75rem;
}

.schema-form-group legend {
    font-weight: 700;
    padding: 0 0.25rem;
}

.schema-form-help {
    color: var(--p-text-muted-color);
}

.schema-form-advanced {
    align-self: flex-start;
}
</style>
